import { assertValue, getFormattedDateMonth, isDefined } from '@libs/utils';
import {
  SemboDate,
  DateFormat,
  weekdays,
  getFirstWeekdayJsIndex,
} from '@libs/sembo-dates';
import type { AppSingleSelectOption } from '@libs/ui';
import type { ItineraryWithPrice } from '@libs/trip-planner-interfaces';
import { isFeatureToggleActive } from '@libs/feature-toggle';

import {
  PriceRibbonDayTileType,
  PriceCalendarState,
  PriceCalendarTileType,
  TileSelectionType,
  calculatePriceMaskBoundries,
  getPriceMaskType,
  getCurrentMonthPastDayTile,
  getCurrentMonthLoaderDayTile,
} from '../../../modules/calendars';
import type {
  PriceRibbonDay,
  PriceRibbonDayTile,
  PriceRibbonMonth,
  CurrentMonthAlternativeProductDayTile,
  CurrentMonthAvailableDayTile,
  CurrentMonthUnavailableDayTile,
  PriceMaskBoundaries,
} from '../../../modules/calendars';
import type { DateWithItinerary } from '../trip/tripStore.model';

export function getMonthsSelectOptions(
  months: Date[]
): AppSingleSelectOption<string>[] {
  return months.map(
    (month: Date): AppSingleSelectOption<string> => ({
      id: formatDate(month, DateFormat.ISO),
      label: `${formatDate(month, DateFormat.MonthFullName)} ${formatDate(
        month,
        DateFormat.FullYear
      )}`,
    })
  );
}

export function getSelectedMonthOption(
  monthsSelectOptions: AppSingleSelectOption<string>[],
  isDateInExpandedMonth: (date: Date) => boolean
): AppSingleSelectOption<string> {
  const monthsSelectedOption = monthsSelectOptions[0];

  assertValue(
    monthsSelectedOption,
    'priceCalendarLogic.ts > getSelectedMonthOption > monthsSelectedOption'
  );

  return (
    monthsSelectOptions.find((option: AppSingleSelectOption<string>) =>
      isDateInExpandedMonth(new Date(option.id))
    ) ?? monthsSelectedOption
  );
}

export function getCalendarTriggerText(
  preferredDay: Date | undefined,
  selectedMonthOption: AppSingleSelectOption<string>
): string {
  return preferredDay
    ? new SemboDate(preferredDay).format(DateFormat.DateWithFullMonth)
    : selectedMonthOption.label;
}

interface GetPriceRibbonMonthsParams {
  months: Date[];
  itineraryDates: DateWithItinerary[];
  isSearchingItineraries: boolean;
  isSelectedItinerary: (date: Date) => boolean;
  isDateInExpandedMonth: (date: Date) => boolean;
  selectedItinerary: ItineraryWithPrice | undefined;
  moneyFormanterFn: (amount: number, currency: string) => string;
}

export function getPriceRibbonMonths({
  months,
  itineraryDates,
  isSearchingItineraries,
  isSelectedItinerary,
  isDateInExpandedMonth,
  selectedItinerary,
  moneyFormanterFn,
}: GetPriceRibbonMonthsParams): PriceRibbonMonth[] {
  function mapItineraryDatesToPriceCalendarDays(
    dateWithItinerary: DateWithItinerary[]
  ): PriceRibbonDay[] {
    const priceMaskBoundaries =
      calculatePriceMaskBoundriesFromItineraryDates(itineraryDates);

    return dateWithItinerary.map((date: DateWithItinerary): PriceRibbonDay => {
      const weekday = new SemboDate(date.date).format(DateFormat.FullDayName);
      const currentDate = new SemboDate(date.date);

      assertValue(
        weekday,
        `priceCalendarLogic.ts > getPriceRibbonMonths > weekday > ${new SemboDate(
          date.date
        ).getDay()}`
      );

      const isSuggested = selectedItinerary
        ? currentDate.format(DateFormat.Date) ===
          new SemboDate(selectedItinerary.date).format(DateFormat.Date)
        : false;

      return {
        date: currentDate.format(DateFormat.ISO),
        dayMonth: getFormattedDateMonth(date.date),
        weekday,
        isSelected: isSelectedItinerary(date.date),
        isSuggested: isSuggested && !isSelectedItinerary(date.date),
        state: getPriceCalendarDayTile({
          date,
          priceMaskBoundaries,
          isSearchingItineraries,
          moneyFormanterFn,
        }),
      };
    });
  }

  return months.map((month: Date) => {
    const isExtended = isDateInExpandedMonth(new Date(month));

    return {
      month: formatDate(month, DateFormat.MonthFullName),
      monthShort: formatDate(month, DateFormat.MonthShortName),
      year: formatDate(month, DateFormat.FullYear),
      isExtended,
      firstDayInMonthISO: month.toISOString(),
      days: isExtended
        ? mapItineraryDatesToPriceCalendarDays(itineraryDates)
        : [],
    };
  });
}

interface GetPriceCalendarDayTileParams {
  date: DateWithItinerary;
  priceMaskBoundaries: PriceMaskBoundaries;
  isSearchingItineraries: boolean;
  moneyFormanterFn: (amount: number, currency: string) => string;
}

export function getPriceCalendarDayTile({
  date,
  priceMaskBoundaries,
  isSearchingItineraries,
  moneyFormanterFn,
}: GetPriceCalendarDayTileParams): PriceRibbonDayTile {
  if (
    (isSearchingItineraries && !date.itinerary) ||
    isFeatureToggleActive('loader')
  ) {
    return {
      type: PriceRibbonDayTileType.LoadingPrice,
    };
  } else if (date.itinerary?.totalPrice) {
    return {
      type: PriceRibbonDayTileType.AvailablePrice,
      priceMaskType: getPriceMaskType(
        priceMaskBoundaries,
        date.itinerary.totalPrice.amount
      ),
      priceWithCurrency: moneyFormanterFn(
        date.itinerary.totalPrice.amount,
        date.itinerary.totalPrice.currency
      ),
    };
  } else if (date.itinerary?.totalPriceWithAlternative) {
    return {
      type: PriceRibbonDayTileType.AlternativePrice,
      priceMaskType: getPriceMaskType(
        priceMaskBoundaries,
        date.itinerary.totalPriceWithAlternative.amount
      ),
      priceWithCurrency: moneyFormanterFn(
        date.itinerary?.totalPriceWithAlternative.amount,
        date.itinerary?.totalPriceWithAlternative.currency
      ),
    };
  } else if (isDefined(date.itinerary) && !date.itinerary.isSearched) {
    return { type: PriceRibbonDayTileType.NotSearched };
  } else {
    return {
      type: PriceRibbonDayTileType.NoAvailability,
    };
  }
}

interface GetPriceCalendarTilesParams {
  weekdaysFormat: DateFormat;
  itineraryDates: DateWithItinerary[];
  suggestedDate: Date | undefined;
  selectedDate: Date | undefined;
  fallbackMonthDate: Date;
  alternativeProductMessage: string;
  noAvailabilityMessage: string;
  forceLoader: boolean;
  moneyFormanterFn: (amount: number, currency: string) => string;
  hideAlternativeProductPrices: boolean;
}

export function getPriceCalendarState(
  params: GetPriceCalendarTilesParams
): PriceCalendarState {
  if (!params.itineraryDates.length) {
    return new PriceCalendarState({
      monthDate: params.fallbackMonthDate,
      weekdays: weekdays(params.weekdaysFormat),
      firstWeekdayJsIndex: getFirstWeekdayJsIndex(),
      today: new Date(),
    });
  }

  const startOfToday = new SemboDate(new Date()).startOf('day');
  const priceMaskBoundaries = calculatePriceMaskBoundriesFromItineraryDates(
    params.itineraryDates
  );

  const { itineraryDates, firstDay } = getItineraryDatesWithPastDaysIncluded(
    params.itineraryDates
  );

  const priceCalendarState = new PriceCalendarState({
    monthDate: firstDay.date,
    weekdays: weekdays(params.weekdaysFormat),
    firstWeekdayJsIndex: getFirstWeekdayJsIndex(),
    today: new Date(),
  });

  itineraryDates.forEach((date: DateWithItinerary) => {
    const currentDate = new SemboDate(date.date);
    const isCurrentDayInThePast = currentDate.isBefore(startOfToday.date);

    if (isCurrentDayInThePast) {
      const tile = getCurrentMonthPastDayTile(date.date);
      priceCalendarState.setTileForDate(date.date, tile);
      return;
    }

    if (!date.itinerary || params.forceLoader) {
      const tile = getCurrentMonthLoaderDayTile(date.date);
      priceCalendarState.setTileForDate(date.date, tile);
      return;
    }

    if (date.itinerary.totalPrice) {
      const tile: CurrentMonthAvailableDayTile = {
        type: PriceCalendarTileType.CurrentMonthAvailableDay,
        text: date.date.getDate().toString(),
        price: {
          type: getPriceMaskType(
            priceMaskBoundaries,
            date.itinerary.totalPrice.amount
          ),
          value: params.moneyFormanterFn(
            date.itinerary.totalPrice.amount,
            date.itinerary.totalPrice.currency
          ),
        },
        selection: getTileSelectionType({
          currentDate: date.date,
          suggestedDate: params.suggestedDate,
          selectedDate: params.selectedDate,
        }),
        date: date.date,
      };
      priceCalendarState.setTileForDate(date.date, tile);
      return;
    }

    if (date.itinerary.totalPriceWithAlternative) {
      const price = params.hideAlternativeProductPrices
        ? undefined
        : {
            type: getPriceMaskType(
              priceMaskBoundaries,
              date.itinerary.totalPriceWithAlternative.amount
            ),
            value: params.moneyFormanterFn(
              date.itinerary.totalPriceWithAlternative.amount,
              date.itinerary.totalPriceWithAlternative.currency
            ),
          };

      const tile: CurrentMonthAlternativeProductDayTile = {
        type: PriceCalendarTileType.CurrentMonthAlternativeProductDay,
        text: date.date.getDate().toString(),
        price,
        selection: getTileSelectionType({
          currentDate: date.date,
          suggestedDate: params.suggestedDate,
          selectedDate: params.selectedDate,
        }),
        date: date.date,
        reason: params.alternativeProductMessage,
      };
      priceCalendarState.setTileForDate(date.date, tile);
      return;
    }

    const tile: CurrentMonthUnavailableDayTile = {
      type: PriceCalendarTileType.CurrentMonthUnavailableDay,
      text: date.date.getDate().toString(),
      selection: getTileSelectionType({
        currentDate: date.date,
        suggestedDate: params.suggestedDate,
        selectedDate: params.selectedDate,
      }),
      date: date.date,
      reason: params.noAvailabilityMessage,
    };
    priceCalendarState.setTileForDate(date.date, tile);
  });

  return priceCalendarState;
}

// TODO: test
function getItineraryDatesWithPastDaysIncluded(
  itineraryDates: DateWithItinerary[]
): {
  itineraryDates: DateWithItinerary[];
  firstDay: DateWithItinerary;
} {
  let firstDay = itineraryDates[0];
  assertValue(
    firstDay,
    'priceCalendarLogic.ts > getPriceCalendarTiles > firstDay'
  );
  let updatedItineraryDates = itineraryDates;

  if (firstDay.date.getDate() > 1) {
    const datesBefore = getAllDatesBefore(firstDay.date);
    const pastDates: DateWithItinerary[] = datesBefore.map((date: Date) => ({
      date,
      itinerary: undefined,
    }));
    updatedItineraryDates = [...pastDates, ...updatedItineraryDates];
    firstDay = updatedItineraryDates[0];
    assertValue(
      firstDay,
      'priceCalendarLogic.ts > getPriceCalendarTiles > firstDay'
    );
  }

  return { itineraryDates: updatedItineraryDates, firstDay };
}

// TODO: test
function getAllDatesBefore(date: Date): Date[] {
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();

  return Array.from(
    { length: day - 1 },
    (_, index: number) => new Date(year, month, index + 1)
  );
}

export interface GetTileSelectionTypeParams {
  currentDate: Date;
  suggestedDate: Date | undefined;
  selectedDate: Date | undefined;
}

export function getTileSelectionType(
  params: GetTileSelectionTypeParams
): TileSelectionType {
  if (
    params.selectedDate &&
    new SemboDate(params.selectedDate).format(DateFormat.Day) ===
      new SemboDate(params.currentDate).format(DateFormat.Day)
  ) {
    return TileSelectionType.Selected;
  }

  if (
    params.suggestedDate &&
    new SemboDate(params.suggestedDate).format(DateFormat.Day) ===
      new SemboDate(params.currentDate).format(DateFormat.Day)
  ) {
    return TileSelectionType.Suggested;
  }

  return TileSelectionType.NotSelected;
}

// TODO: Test - next iteration
function calculatePriceMaskBoundriesFromItineraryDates(
  itineraryDates: DateWithItinerary[]
): PriceMaskBoundaries {
  const prices: number[] = itineraryDates.reduce(
    (accummulator: number[], date: DateWithItinerary) => {
      if (!date.itinerary) {
        return accummulator;
      }
      const price =
        date.itinerary.totalPrice?.amount ||
        date.itinerary.totalPriceWithAlternative?.amount;
      if (price) {
        return [...accummulator, price];
      }
      return accummulator;
    },
    []
  );

  return calculatePriceMaskBoundries(prices);
}

function formatDate(month: Date, format: DateFormat): string {
  const date = new SemboDate(month).format(format);
  return date.at(0)?.toUpperCase() + date.slice(1);
}
